import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, CheckCircle, AlertTriangle, Loader2, Pencil, X, Clock, User, Filter, Pause, Play, Mail, Ban } from 'lucide-react';
import { getLeadDetail, updateLead, getDripEnrollment, pauseDrip, resumeDrip, enrollDrip } from '../lib/api';
import OpsLayout from '../components/OpsLayout';

const TIMELINE_LABELS = { '0_3': '0–3 Months', '3_6': '3–6 Months', '6_12': '6–12 Months', 'researching': 'Just Researching' };

const ACTION_LABELS = {
  status_changed: 'Status changes',
  field_edited: 'Field edits',
  note_added: 'Notes added',
};

function AuditLog({ entries = [] }) {
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');

  // Get unique users from entries
  const users = [...new Set(entries.map(e => e.by).filter(Boolean))];
  const actions = [...new Set(entries.map(e => e.action).filter(Boolean))];

  const filtered = [...entries].reverse().filter(e => {
    if (filterAction !== 'all' && e.action !== filterAction) return false;
    if (filterUser !== 'all' && e.by !== filterUser) return false;
    return true;
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="audit-log-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>Audit Log</h2>
        <span className="font-mono text-xs text-slate-400">{filtered.length} of {entries.length}</span>
      </div>

      {entries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-slate-100">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <Select value={filterAction} onValueChange={setFilterAction}>
            <SelectTrigger className="h-7 w-[150px] text-xs rounded-lg bg-slate-50 border-slate-200" data-testid="audit-filter-action">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {actions.map(a => (
                <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="h-7 w-[200px] text-xs rounded-lg bg-slate-50 border-slate-200" data-testid="audit-filter-user">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {users.map(u => (
                <SelectItem key={u} value={u}>{u}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filterAction !== 'all' || filterUser !== 'all') && (
            <button onClick={() => { setFilterAction('all'); setFilterUser('all'); }} className="text-[11px] text-slate-400 hover:text-slate-700 underline">
              Clear filters
            </button>
          )}
        </div>
      )}

      {filtered.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filtered.map((entry, i) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                entry.action === 'status_changed' ? 'bg-purple-50' :
                entry.action === 'note_added' ? 'bg-blue-50' :
                'bg-slate-50'
              }`}>
                {entry.action === 'status_changed' ? <Clock className="w-3 h-3 text-purple-500" /> :
                 entry.action === 'note_added' ? <Save className="w-3 h-3 text-blue-500" /> :
                 <Pencil className="w-3 h-3 text-slate-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-slate-700">
                  {entry.action === 'status_changed' && <><span className="font-medium">Status</span> changed from <span className="font-mono text-xs bg-slate-100 px-1 rounded">{entry.old}</span> to <span className="font-mono text-xs bg-slate-100 px-1 rounded">{entry.new}</span></>}
                  {entry.action === 'note_added' && <><span className="font-medium">Note added:</span> {entry.new}</>}
                  {entry.action === 'field_edited' && <><span className="font-medium">{entry.field}</span> changed from <span className="font-mono text-xs bg-slate-100 px-1 rounded">{entry.old || '(empty)'}</span> to <span className="font-mono text-xs bg-slate-100 px-1 rounded">{entry.new}</span></>}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <User className="w-3 h-3 text-slate-300" />
                  <span className="text-[11px] text-slate-400">{entry.by}</span>
                  <span className="text-[11px] text-slate-300">&middot;</span>
                  <span className="text-[11px] text-slate-400 font-mono">{entry.ts ? new Date(entry.ts).toLocaleString() : ''}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : entries.length > 0 ? (
        <p className="text-sm text-slate-400">No entries match your filters.</p>
      ) : (
        <p className="text-sm text-slate-400">No activity recorded yet. Changes to this lead will be logged here.</p>
      )}
    </div>
  );
}

function EditableField({ label, value, fieldKey, onSave, type = 'text', options = null }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave(fieldKey, type === 'number' ? Number(draft) : draft);
    setSaving(false);
    setEditing(false);
  };

  if (!editing) {
    const display = options ? (options.find(o => o.value === value)?.label || value) : value;
    return (
      <div className="group flex items-center justify-between py-1.5">
        <div>
          <p className="text-[10px] text-slate-400 uppercase tracking-widest">{label}</p>
          <p className="text-sm font-medium text-slate-900">{display || '—'}</p>
        </div>
        <button onClick={() => { setDraft(value || ''); setEditing(true); }} className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-slate-100" data-testid={`edit-${fieldKey}`}>
          <Pencil className="w-3 h-3 text-slate-400" />
        </button>
      </div>
    );
  }

  return (
    <div className="py-1.5">
      <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <div className="flex items-center gap-2">
        {options ? (
          <Select value={draft} onValueChange={setDraft}>
            <SelectTrigger className="h-8 text-sm rounded-lg flex-1"><SelectValue /></SelectTrigger>
            <SelectContent>{options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Input value={draft} onChange={e => setDraft(e.target.value)} type={type} className="h-8 text-sm rounded-lg flex-1" />
        )}
        <Button size="sm" onClick={save} disabled={saving} className="h-8 w-8 p-0 rounded-lg bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 className="w-3 h-3 animate-spin text-white" /> : <Save className="w-3 h-3 text-white" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8 w-8 p-0 rounded-lg">
          <X className="w-3 h-3 text-slate-400" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [drip, setDrip] = useState(null);
  const [dripLoading, setDripLoading] = useState(false);
  const role = localStorage.getItem('admin_role') || 'admin';

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) { navigate('/ops/login'); return; }
    fetchLead();
  }, [id]);

  const fetchLead = async () => {
    setLoading(true);
    try {
      const r = await getLeadDetail(id);
      setLead(r.data);
      // Fetch drip enrollment
      getDripEnrollment(id).then(dr => setDrip(dr.data?.enrollment)).catch(() => {});
    } catch (e) {
      if (e.response?.status === 401) navigate('/ops/login');
    }
    setLoading(false);
  };

  const handleFieldSave = async (fieldKey, value) => {
    try {
      const r = await updateLead(id, { [fieldKey]: value });
      setLead(r.data);
    } catch (e) { console.error('Update failed', e); }
  };

  const handleStatusChange = async (status) => {
    try { const r = await updateLead(id, { status }); setLead(r.data); } catch (e) { console.error(e); }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { const r = await updateLead(id, { note: note.trim() }); setLead(r.data); setNote(''); } catch (e) { console.error(e); }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!lead) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><p className="text-slate-500">Lead not found</p></div>;

  const { lead: contact = {}, answers = {}, matching = {}, intent = {}, lifecycle = {}, admin_notes = [], audit_log = [] } = lead;

  return (
    <OpsLayout>
    <div className="min-h-screen bg-slate-50" data-testid="admin-lead-detail-page">
      <header className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/ops/leads')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-leads">
            <ArrowLeft className="w-4 h-4" /> Back to Leads
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">Status:</span>
            <Select value={lifecycle.status || 'new'} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[140px] bg-white rounded-lg h-8 text-xs" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="nurturing">Nurturing</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Info — Editable */}
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="contact-section">
              <h2 className="text-lg font-medium mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>Contact Info</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <EditableField label="Name" value={contact.first_name} fieldKey="first_name" onSave={handleFieldSave} />
                <EditableField label="Email" value={contact.email} fieldKey="email" onSave={handleFieldSave} />
                <EditableField label="Phone" value={contact.phone} fieldKey="phone" onSave={handleFieldSave} />
                <EditableField label="Preferred Contact" value={contact.preferred_contact} fieldKey="preferred_contact" onSave={handleFieldSave} options={[{value:'email',label:'Email'},{value:'phone',label:'Phone'}]} />
              </div>
            </div>

            {/* Buyer Profile — Editable */}
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="intent-section">
              <h2 className="text-lg font-medium mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>Buyer Profile</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 mb-4">
                <div className="py-1.5">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest">Intent Score</p>
                  <p className="font-mono text-2xl font-semibold">{intent.score ?? '—'}<span className="text-sm text-slate-400">/100</span></p>
                </div>
                <div className="py-1.5">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest">Tier</p>
                  <span className={`text-xs font-mono px-2 py-0.5 border ${intent.tier === 'hot' ? 'bg-green-50 text-green-700 border-green-200' : intent.tier === 'warm' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{intent.tier || '—'}</span>
                </div>
                <EditableField label="Timeline" value={answers.timeline} fieldKey="timeline" onSave={handleFieldSave} options={[{value:'0_3',label:'0–3 Months'},{value:'3_6',label:'3–6 Months'},{value:'6_12',label:'6–12 Months'},{value:'researching',label:'Just Researching'}]} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1">
                <EditableField label="Budget Min" value={answers.budget_min} fieldKey="budget_min" onSave={handleFieldSave} type="number" />
                <EditableField label="Budget Max" value={answers.budget_max} fieldKey="budget_max" onSave={handleFieldSave} type="number" />
                <EditableField label="Property Type" value={answers.property_type} fieldKey="property_type" onSave={handleFieldSave} options={[{value:'detached',label:'Detached'},{value:'town',label:'Townhome'},{value:'condo',label:'Condo'},{value:'any',label:'Any'}]} />
                <EditableField label="Pre-approval" value={answers.preapproval_status} fieldKey="preapproval_status" onSave={handleFieldSave} options={[{value:'yes',label:'Yes'},{value:'in_progress',label:'In Progress'},{value:'no',label:'No'}]} />
                <div className="py-1.5">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest">Origin</p>
                  <p className="text-sm font-medium text-slate-900">{answers.relocation_origin || '—'}</p>
                </div>
                <div className="py-1.5">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest">Working with Realtor</p>
                  <p className="text-sm font-medium text-slate-900">{answers.working_with_realtor ? 'Yes' : 'No'}</p>
                </div>
              </div>
              {answers.priorities?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Priorities</p>
                  <div className="flex flex-wrap gap-1.5">
                    {answers.priorities.map(p => <span key={p} className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{p.replace(/_/g, ' ')}</span>)}
                  </div>
                </div>
              )}
              {intent.signals?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-2">Signals</p>
                  <div className="flex flex-wrap gap-1.5">
                    {intent.signals.map((s, i) => <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{s}</span>)}
                  </div>
                </div>
              )}
            </div>

            {/* Match Results */}
            {matching.neighbourhood_scores?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="matches-section">
                <h2 className="text-lg font-medium mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>Match Results</h2>
                <div className="space-y-3">
                  {matching.neighbourhood_scores.map((m, i) => (
                    <div key={i} className="border border-slate-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium">{m.name}</h4>
                        <span className={`font-mono text-sm font-medium px-2 py-0.5 rounded ${m.score >= 70 ? 'bg-green-50 text-green-700' : m.score >= 40 ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-600'}`}>{m.score}/100</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>{m.reasons?.map((r, j) => <div key={j} className="flex items-start gap-1.5 mb-1"><CheckCircle className="w-3 h-3 text-green-600 mt-0.5" /><span className="text-xs text-slate-600">{r}</span></div>)}</div>
                        <div>{m.tradeoffs?.map((t, j) => <div key={j} className="flex items-start gap-1.5 mb-1"><AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5" /><span className="text-xs text-slate-600">{t}</span></div>)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Audit Log */}
            <AuditLog entries={audit_log} />
          </div>

          {/* Right sidebar */}
          <div className="space-y-6">
            {/* Notes */}
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="admin-notes-section">
              <h2 className="text-lg font-medium mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>Notes</h2>
              {admin_notes?.length > 0 ? (
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {admin_notes.map((n, i) => (
                    <div key={i} className="bg-slate-50 p-3 border border-slate-100 rounded-lg">
                      <p className="text-sm text-slate-700">{n.note}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-slate-400">{n.author}</span>
                        <span className="text-xs text-slate-300">&middot;</span>
                        <span className="text-xs text-slate-400 font-mono">{n.ts ? new Date(n.ts).toLocaleDateString() : ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 mb-4">No notes added.</p>
              )}
              <Textarea placeholder="Add a note..." value={note} onChange={(e) => setNote(e.target.value)} className="bg-white rounded-lg text-sm min-h-[80px] mb-3" data-testid="note-textarea" />
              <Button onClick={handleAddNote} disabled={saving || !note.trim()} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-lg h-9 text-sm" data-testid="save-note-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Note
              </Button>
            </div>

            {/* Consent */}
            {contact.consent && (
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="consent-section">
                <h3 className="text-sm font-medium mb-3">Consent</h3>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>Marketing opt-in: {contact.consent.marketing_opt_in ? 'Yes' : 'No'}</p>
                  <p className="font-mono">{contact.consent.consent_timestamp ? new Date(contact.consent.consent_timestamp).toLocaleString() : ''}</p>
                </div>
              </div>
            )}

            {/* Drip Email Controls */}
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="drip-section">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-slate-500" />
                <h3 className="text-sm font-medium">Drip Sequence</h3>
              </div>
              {drip ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${
                      drip.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      drip.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      drip.status === 'paused' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-slate-50 text-slate-600 border-slate-200'
                    }`}>{drip.status}</span>
                  </div>
                  <div className="space-y-1.5 mb-4">
                    {drip.scheduled_emails?.map((e, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-slate-500">Email {e.step}</span>
                        <span className={`font-mono ${
                          e.status === 'sent' ? 'text-emerald-600' :
                          e.status === 'cancelled' ? 'text-slate-400 line-through' :
                          e.status === 'scheduled' ? 'text-blue-600' :
                          'text-red-500'
                        }`}>{e.status}{e.sent_at ? ` · ${new Date(e.sent_at).toLocaleDateString()}` : ''}</span>
                      </div>
                    ))}
                  </div>
                  {drip.status === 'active' && (
                    <Button
                      onClick={async () => { setDripLoading(true); try { await pauseDrip(id, 'Paused by realtor'); const dr = await getDripEnrollment(id); setDrip(dr.data?.enrollment); fetchLead(); } catch(e) { alert(e.response?.data?.detail || e.message); } setDripLoading(false); }}
                      disabled={dripLoading}
                      variant="outline"
                      className="w-full rounded-lg h-8 text-xs gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50"
                      data-testid="pause-drip-btn"
                    >
                      <Pause className="w-3 h-3" /> Pause Drip
                    </Button>
                  )}
                  {drip.status === 'paused' && !lead?.unsubscribed && (
                    <Button
                      onClick={async () => { setDripLoading(true); try { await resumeDrip(id); const dr = await getDripEnrollment(id); setDrip(dr.data?.enrollment); fetchLead(); } catch(e) { alert(e.response?.data?.detail || e.message); } setDripLoading(false); }}
                      disabled={dripLoading}
                      variant="outline"
                      className="w-full rounded-lg h-8 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid="resume-drip-btn"
                    >
                      <Play className="w-3 h-3" /> Resume Drip
                    </Button>
                  )}
                  {drip.drip_paused_by_realtor && drip.drip_pause_reason && (
                    <p className="text-[10px] text-slate-400 mt-2 italic">{drip.drip_pause_reason}</p>
                  )}
                </div>
              ) : lead?.drip_status === 'ineligible' ? (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Ban className="w-3.5 h-3.5" />
                  <span>Ineligible for drip{lead?.drip_ineligible_reason ? ` (${lead.drip_ineligible_reason.replace(/_/g, ' ')})` : ''}</span>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-slate-400 mb-3">Not enrolled in drip sequence.</p>
                  <Button
                    onClick={async () => { setDripLoading(true); try { await enrollDrip(id); const dr = await getDripEnrollment(id); setDrip(dr.data?.enrollment); fetchLead(); } catch(e) { alert(e.response?.data?.detail || e.message); } setDripLoading(false); }}
                    disabled={dripLoading}
                    variant="outline"
                    className="w-full rounded-lg h-8 text-xs gap-1.5"
                    data-testid="enroll-drip-btn"
                  >
                    <Mail className="w-3 h-3" /> Enroll in Drip
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </OpsLayout>
  );
}
